import assignmentService from 'ApiService/Student/assignmentService';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from './Steps/Model';
import UploadSheets from './Steps/UploadSheets';

const Index = ({ selectedAssignment, handleBackClick }) => {
  const [submissiontext, setSubmissiontext] = useState('');
  const { state } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disable, setDisable] = useState(true);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [existingErrors, setExistingErrors] = useState([]);
  const [getFiles, setGetFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshFiles, setRefreshFiles] = useState(false);
  const [hasSubmittedAssignment, setHasSubmittedAssignment] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Confirmation modal states
  const [isUploadConfirmationModalOpen, setIsUploadConfirmationModalOpen] =
    useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);
  const [isEditConfirmationModalOpen, setIsEditConfirmationModalOpen] =
    useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const handleFileClick = (file, filePath) => {
    if (filePath) {
      window.open(filePath, '_blank');
    } else {
      console.error('File path is invalid');
    }

    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL.replace(
    '/api/v1',
    ''
  )}`;

  const allowedExtensions = [
    'jpeg',
    'png',
    'jpg',
    'mp4',
    'mkv',
    'avi',
    'mov',
    'pdf',
    'docx',
    'txt',
    'pptx',
  ];

  const handleFileChange = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      const fileExtension = uploadedFile.name.split('.').pop().toLowerCase();
      const maxFileSize = 20 * 1024 * 1024; // 20 MB, matching backend

      if (!allowedExtensions.includes(fileExtension)) {
        toast.error(
          `Invalid file format. Allowed formats: ${allowedExtensions.join(
            ', '
          )}`
        );
        return;
      }

      if (uploadedFile.size > maxFileSize) {
        toast.error(
          'File size exceeds the 20 MB limit. Please upload a smaller file.'
        );
        return;
      }

      setFile(uploadedFile);
      setFileUploaded(true);
      toast.success('File selected successfully!');
    } else {
      toast.error('No file selected. Please attach a valid file.');
    }
  };

  // Show confirmation modal before actual submission
  const handleUploadClick = () => {
    if (!selectedAssignment?._id) {
      toast.error('No assignment selected. Please try again.');
      return;
    }

    setIsUploadConfirmationModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await assignmentService.uploadAssignment({
        assignmentId: selectedAssignment?._id,
        answer: submissiontext,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);
        setFile(null);
        setFileUploaded(false);
        setRefreshFiles((prev) => !prev);
        setIsEditMode(false);
        setIsUploadConfirmationModalOpen(false);

        // Immediately update local state to reflect the new file
        await fetchData();

        if (response.data.error && response.data.error.length) {
          setExistingErrors(response.data.error);
        } else {
          setExistingErrors([]);
        }
      } else {
        throw new Error(
          response?.data?.message || 'Unexpected server response.'
        );
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      toast.error('Error uploading file: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handler to open delete confirmation modal
  const confirmFileDelete = (filename) => {
    setFileToDelete(filename);
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleFileDelete = async () => {
    if (!fileToDelete) return;

    try {
      setLoading(true);
      const response = await assignmentService.deleteuploadedAssignment({
        filename: fileToDelete,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);

        setGetFiles((prevFiles) =>
          prevFiles.filter((file) => file.filename !== fileToDelete)
        );

        await fetchData();

        setIsModalOpen(false);
        setIsDeleteConfirmationModalOpen(false);
        setFileToDelete(null);

        setHasSubmittedAssignment(false);
        setIsEditMode(false);
      } else {
        throw new Error(response?.data?.message || 'Failed to delete file.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      toast.error('Error deleting file: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmEditMode = () => {
    setIsEditConfirmationModalOpen(true);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setFile(null);
    setFileUploaded(false);
    setIsEditConfirmationModalOpen(false);
  };

  const fetchData = async () => {
    try {
      const response = await assignmentService.getuploadedAssignments(
        selectedAssignment._id
      );
      if (response.data.success) {
        const uploadedFiles = response.data.data || [];

        setGetFiles(uploadedFiles);

        setHasSubmittedAssignment(uploadedFiles.length > 0);

        if (uploadedFiles.length === 0) {
          setIsEditMode(false);
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          'Failed to fetch uploaded assignments.'
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [state.user, refreshFiles, selectedAssignment._id]);

  return (
    <>
      <div className="mt-3 h-full w-full">
        <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
        <div className="md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
          <Card extra="-mt-[280px] w-full md:-mt-[240px] md:px-[70px] h-full mx-auto pb-3">
            {(isEditMode || !hasSubmittedAssignment) && (
              <>
                <div className="rounded-[20px]">
                  <UploadSheets
                    submissiontext={submissiontext}
                    setSubmissiontext={setSubmissiontext}
                    handleFileChange={handleFileChange}
                    setDisable={setDisable}
                    fileUploaded={fileUploaded}
                    setFileUploaded={setFileUploaded}
                  />
                </div>
                <div className="mt-4 flex flex-col justify-end px-3 pt-2 md:flex-row md:px-8">
                  <button
                    disabled={loading}
                    onClick={handleUploadClick}
                    className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 md:ml-2"
                  >
                    {loading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </>
            )}

            {hasSubmittedAssignment && !isEditMode && (
              <div className="flex flex-col items-center justify-center">
                <div className="py-4 text-center font-bold text-green-600">
                  Assignment Already Submitted : )
                </div>
              </div>
            )}

            <>
              <div className="mt-3 h-full w-full">
                <div className="md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
                  <div className="grid-row-1 lg:grid-row-1 mt-4 grid gap-4 sm:grid-cols-2">
                    {getFiles.length === 0 ? (
                      <p className="col-span-full text-center text-gray-600">
                        No files available
                      </p>
                    ) : (
                      getFiles.map((data, index) => {
                        if (!data.filename) {
                          return (
                            <div
                              key={index}
                              className="col-span-full text-center text-gray-600"
                            ></div>
                          );
                        }
                        const fileExtension = data.filename
                          .split('.')
                          .pop()
                          .toLowerCase();
                        const filePath = `${BASE_URL}/${data.filepath}`;

                        return (
                          <div key={index} className="relative cursor-pointer">
                            {/* File Display Based on Type */}
                            {['png', 'jpeg', 'jpg'].includes(fileExtension) && (
                              <img
                                src={filePath}
                                alt={data.filename}
                                className="h-40 w-full rounded-md object-cover transition hover:opacity-75"
                                onClick={() =>
                                  handleFileClick({
                                    type: 'image',
                                    src: filePath,
                                    filename: data.filename,
                                  })
                                }
                              />
                            )}
                            {['pdf'].includes(fileExtension) && (
                              <div
                                className="flex h-40 w-full cursor-pointer items-center justify-center rounded-md bg-gray-200"
                                onClick={() => handleFileClick(filePath)}
                              >
                                <span className="text-sm font-medium">
                                  View PDF
                                </span>
                              </div>
                            )}

                            {['mp4', 'mkv', 'avi', 'mov'].includes(
                              fileExtension
                            ) && (
                              <video
                                controls
                                className="h-40 w-full rounded-md transition hover:opacity-75"
                                onClick={() =>
                                  handleFileClick({
                                    type: 'video',
                                    src: filePath,
                                    filename: data.filename,
                                  })
                                }
                              >
                                <source
                                  src={filePath}
                                  type={`video/${fileExtension}`}
                                />
                                Your browser does not support the video tag.
                              </video>
                            )}

                            {![
                              'png',
                              'jpeg',
                              'jpg',
                              'pdf',
                              'mp4',
                              'mkv',
                              'avi',
                              'mov',
                            ].includes(fileExtension) && (
                              <div className="col-span-full text-center text-gray-600">
                                Unsupported file type: {data.filename}
                              </div>
                            )}

                            <button
                              onClick={() => confirmFileDelete(data.filename)}
                              className="absolute right-1 top-2 rounded bg-red-600 px-2 py-1 text-sm font-medium text-white transition hover:bg-red-500 active:bg-red-700"
                            >
                              Delete
                            </button>
                            <button
                              onClick={confirmEditMode}
                              className="absolute right-20 top-2 rounded bg-brand-500 px-2 py-1 text-sm font-medium text-white transition hover:bg-brand-500 active:bg-brand-700"
                            >
                              Edit
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* File Viewer Modal */}
              <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                file={selectedFile}
              />

              {/* Upload Confirmation Modal */}
              <ConfirmationModal
                isOpen={isUploadConfirmationModalOpen}
                onClose={() =>
                  !loading && setIsUploadConfirmationModalOpen(false)
                }
                onConfirm={handleSubmit}
                title="Confirm Assignment Submission"
                message="Are you sure you want to submit this assignment? This action cannot be undone."
                confirmText={loading ? 'Uploading...' : 'Yes, Submit'}
                cancelText="Cancel"
                isSubmitting={loading}
                icon="warning"
              />

              {/* Delete Confirmation Modal */}
              <ConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                onClose={() =>
                  !loading && setIsDeleteConfirmationModalOpen(false)
                }
                onConfirm={handleFileDelete}
                title="Confirm File Deletion"
                message="Are you sure you want to delete this file? This action cannot be undone."
                confirmText={loading ? 'Deleting...' : 'Yes, Delete File'}
                cancelText="Cancel"
                isSubmitting={loading}
                icon="warning"
              />

              {/* Edit Confirmation Modal */}
              <ConfirmationModal
                isOpen={isEditConfirmationModalOpen}
                onClose={() =>
                  !loading && setIsEditConfirmationModalOpen(false)
                }
                onConfirm={handleEditClick}
                title="Confirm Edit Mode"
                message="Switching to edit mode will allow you to replace your current submission. Do you want to continue?"
                confirmText="Yes, Switch to Edit Mode"
                cancelText="Cancel"
                isSubmitting={loading}
                icon="warning"
              />
            </>

            <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
              <button
                onClick={handleBackClick}
                className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
              >
                Prev
              </button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Index;
